<script setup>
/**
 * FormSaveCloseBtn
 * Persists form data and navigates away from the form upon success.
 * Handles specialized redirection logic for Site Tree content and
 * ensures query parameters are preserved during the transition.
 */
import { computed, getCurrentInstance } from 'vue';
import Button from "../Button.vue";

const props = defineProps({
    formId: {
        type: String,
        required: true
    }
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- Computed ---

const formState = computed(() => store.state[props.formId]);

const isModified = computed(() => store.getters[`${props.formId}/isModified`]?.());

const isSaving = computed(() => formState.value?.isSaving);

const tableName = computed(() => formState.value?.tableName || '');

const show = computed(() => {
    return (
        !tableName.value.includes('product_variations__') &&
        tableName.value !== 'files'
    );
});

const isSiteTree = computed(() => proxy.$route.name === 'siteTree');

/**
 * Calculates the destination URL after a successful save.
 */
const onSaveRedirectUrl = computed(() => {
    if (isSiteTree.value) {
        const path = proxy.$route.path;
        const parts = path.split('/');
        const lastPart = parts.pop() || '';
        // Extract ID from format like 'c123'
        const pageContentId = parseInt(lastPart.substring(1));

        const obj = store.state.pageContentData.items.find(o => o.id === pageContentId);
        return obj?.pageId ? `/#/site_tree/${obj.pageId}` : '/#/site_tree';
    }

    let url = `/#/${tableName.value}`;
    const search = document.location.hash.split('?')[1];

    if (search) {
        url += `?${search}`;
    }

    return url;
});

// --- Methods ---

const onClick = () => {
    store.dispatch(`${props.formId}/save`)
        .then(res => {
            // Do not redirect if validation failed
            if (res?.invalid) return;

            window.location.href = onSaveRedirectUrl.value;
        });
};
</script>

<template>
    <Button
        v-if="show"
        :disabled="isSaving"
        class="rounded-0"
        title="Save and close"
        :active-class="'btn-warning'"
        :active-value="isModified"
        @click.native="onClick"
    >
        <i class="bi-chevron-right"/>
    </Button>
</template>